import { useMutation, useQuery, useQueryClient } from "react-query";

import { PromptsService } from "../../pages/dashboard/prompts/services/prompts.service";
import { CreatePromptData, UpdatePromptData } from "../../types/prompts.types";

// Query keys
export const PROMPTS_QUERY_KEYS = {
    all: ["prompts"] as const,
    list: () => [...PROMPTS_QUERY_KEYS.all, "list"] as const,
    detail: (id: string) => [...PROMPTS_QUERY_KEYS.all, "detail", id] as const,
};

// Get all prompts query
export const usePromptsQuery = () => {
    return useQuery({
        queryKey: PROMPTS_QUERY_KEYS.list(),
        queryFn: PromptsService.getPrompts,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Create prompt mutation
export const useCreatePromptMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreatePromptData) => PromptsService.createPrompt(data),
        onSuccess: () => {
            // Invalidate and refetch prompts list
            queryClient.invalidateQueries({ queryKey: PROMPTS_QUERY_KEYS.list() });
        },
    });
};

// Update prompt mutation
export const useUpdatePromptMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdatePromptData }) => PromptsService.updatePrompt(id, data),
        onSuccess: () => {
            // Invalidate and refetch prompts list
            queryClient.invalidateQueries({ queryKey: PROMPTS_QUERY_KEYS.list() });
        },
    });
};

// Delete prompt mutation (for future use)
export const useDeletePromptMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => PromptsService.deletePrompt(id),
        onSuccess: () => {
            // Invalidate and refetch prompts list
            queryClient.invalidateQueries({ queryKey: PROMPTS_QUERY_KEYS.list() });
        },
    });
};

// Get single prompt query (for future use)
export const usePromptQuery = (id: string) => {
    return useQuery({
        queryKey: PROMPTS_QUERY_KEYS.detail(id),
        queryFn: () => PromptsService.getPromptById(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};
